import { ACCESSIBLE_APPS, rolesIncludeContractor, USER_ROLES } from '@workspace/ui/lib';
import type { JwtPayload } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';

function getEorPeopleRedirectUrl(): string {
  const eorBase = process.env.NEXT_PUBLIC_FE_URL_EOR;
  return `${eorBase}/people`;
}

function hasContractorRole(roles?: string[]): boolean {
  return rolesIncludeContractor(roles);
}

function hasB2CStandardOrFreeRole(roles?: string[]): boolean {
  return (
    (roles ?? []).includes(USER_ROLES.B2C_STANDARD) || (roles ?? []).includes(USER_ROLES.B2C_FREE)
  );
}

function hasB2CEmployeeRole(roles?: string[]): boolean {
  return (roles ?? []).includes(USER_ROLES.B2C_EMPLOYEE);
}

function hasTeamsGlobalAdminRole(roles?: string[]): boolean {
  return (roles ?? []).includes(USER_ROLES.TEAMS_GLOBAL_ADMIN);
}

function shouldRedirectToPeoplePage(accessibleApp: string, roles?: string[]): boolean {
  if (!hasTeamsGlobalAdminRole(roles)) return false;

  if (accessibleApp === ACCESSIBLE_APPS.CONTRACTOR) return true;

  // Some users keep `accessible_app: employee` in the JWT while also holding contractor
  // roles. Navigation treats them as contractor global admins (People-first, no dashboard),
  // so post-login must match and send them to the People page instead of the EOR home.
  return accessibleApp === ACCESSIBLE_APPS.EMPLOYEE && hasContractorRole(roles);
}

function shouldRedirectToWalletPage(accessibleApp: string, roles?: string[]): boolean {
  // B2C users do not have a dashboard, so their landing page is Wallet.
  // because they don't have a dashboard yet.
  return (
    accessibleApp === ACCESSIBLE_APPS.EMPLOYEE &&
    (hasB2CEmployeeRole(roles) || hasB2CStandardOrFreeRole(roles))
  );
}

/**
 * Determines the redirect URL based on the accessible app from JWT token
 * @param accessibleApp - The accessible app value from the JWT token
 * @returns The appropriate frontend URL for the user's app
 */

export function getRedirectUrl(
  accessibleApp: string,
  userAccessibleApp?: boolean,
  roles?: string[]
): string {
  if (process.env.ENVIRONMENT_NAME === 'prod' && !userAccessibleApp) {
    return 'https://app.nativeteams.com';
  }

  if (shouldRedirectToPeoplePage(accessibleApp, roles)) {
    return getEorPeopleRedirectUrl();
  }

  if (shouldRedirectToWalletPage(accessibleApp, roles)) {
    return process.env.NEXT_PUBLIC_FE_URL_WALLET || '/';
  }

  // todo: contractor will need to be redirected to the eor app,
  // once the dashboard is ready for the contractors.
  switch (accessibleApp) {
    case ACCESSIBLE_APPS.ADMIN:
      return process.env.NEXT_PUBLIC_FE_URL_ADMIN || '/';
    case ACCESSIBLE_APPS.EMPLOYEE:
      return process.env.NEXT_PUBLIC_FE_URL_EOR || '/';
    case ACCESSIBLE_APPS.EMPLOYER:
      return process.env.NEXT_PUBLIC_FE_URL_EOR || '/';
    case ACCESSIBLE_APPS.WALLET:
    case ACCESSIBLE_APPS.CONTRACTOR:
      return process.env.NEXT_PUBLIC_FE_URL_WALLET || '/';
    case ACCESSIBLE_APPS.GIG_WORKER:
      return process.env.NEXT_PUBLIC_FE_URL_WALLET || '/';
    default:
      // Fallback to the first available URL or localhost
      return process.env.NEXT_PUBLIC_FE_URL_EOR || '/';
  }
}

/**
 * Determines the redirect URL from an access token by decoding it
 * @param accessToken - The JWT access token
 * @returns The appropriate frontend URL for the user's app
 * @throws Error if token is invalid or cannot be decoded
 */
export function getRedirectUrlFromToken(accessToken: string): string {
  if (!accessToken || typeof accessToken !== 'string' || accessToken.trim() === '') {
    throw new Error('Invalid access token: Token is required and must be a non-empty string');
  }

  try {
    const decodedToken = jwtDecode<JwtPayload>(accessToken);

    if (!decodedToken || typeof decodedToken !== 'object') {
      throw new Error('Invalid token: Failed to decode JWT payload');
    }

    if (!decodedToken.accessible_app || typeof decodedToken.accessible_app !== 'string') {
      throw new Error('Invalid token: Missing or invalid accessible_app in JWT payload');
    }

    if (!decodedToken.user?.id || typeof decodedToken.user.id !== 'number') {
      throw new Error('Invalid token: Missing or invalid user.id in JWT payload');
    }

    return getRedirectUrl(
      decodedToken.accessible_app,
      decodedToken.user.new_app_access,
      decodedToken.roles
    );
  } catch (error) {
    if (error instanceof Error && error.message.startsWith('Invalid token:')) {
      throw error; // Re-throw our custom errors
    }
    throw new Error(
      `Failed to decode access token: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Same rules as login page `redirect` query: in-app path only (open redirect safe).
 */
export function getSafeRedirectPath(redirectParam: string | null): string | undefined {
  if (
    redirectParam &&
    typeof redirectParam === 'string' &&
    redirectParam.startsWith('/') &&
    !redirectParam.startsWith('//')
  ) {
    return redirectParam;
  }
  return undefined;
}

/**
 * Merge fe app base URL with an optional pathname (and query), same as login-form.
 */
export function buildAppRedirectUrl(feAppUrl: string, redirectToPage?: string): string {
  const redirectUrl = new URL(feAppUrl);
  if (redirectToPage) {
    const [pathname, search] = redirectToPage.split('?');
    redirectUrl.pathname = pathname || '/';
    if (search) {
      redirectUrl.search = `?${search}`;
    }
  }
  return redirectUrl.toString();
}
